import { create } from 'zustand';
import { NO_FEATURES, type AppFeatures, type AuthUser } from './types';

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';
export type SignOutReason = 'logout' | 'expired' | 'idle';

interface AuthState {
  user: AuthUser | null;
  features: AppFeatures;
  status: AuthStatus;
  /** Why the last session ended — the login page shows a matching notice. */
  signOutReason: SignOutReason | null;
  setUser: (user: AuthUser) => void;
  setFeatures: (features: Partial<AppFeatures> | undefined) => void;
  signOut: (reason: SignOutReason) => void;
  clearReason: () => void;
}

/**
 * Session state. Holds only the user profile from `/auth/me` — the session itself lives in
 * httpOnly cookies, so nothing here is persisted.
 */
export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  features: NO_FEATURES,
  status: 'unknown',
  signOutReason: null,
  setUser: (user) => set({ user, status: 'authenticated', signOutReason: null }),
  setFeatures: (features) => set({ features: { ...NO_FEATURES, ...features } }),
  signOut: (reason) => set({ user: null, status: 'anonymous', signOutReason: reason }),
  clearReason: () => set({ signOutReason: null }),
}));

/** Flaga funkcji z backendu (np. `useFeature('fleetAi')`); false do czasu odpowiedzi `/auth/me`. */
export const useFeature = (key: keyof AppFeatures): boolean => useAuthStore((s) => s.features[key]);

export const useCurrentUser = (): AuthUser | null => useAuthStore((s) => s.user);
